// main.ts
import { CommonModule } from '@angular/common';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi, withXhr } from '@angular/common/http';
import {
  enableProdMode,
  EnvironmentProviders,
  importProvidersFrom,
  inject,
  provideAppInitializer,
  provideCheckNoChangesConfig,
  provideZonelessChangeDetection,
} from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { OVERLAY_DEFAULT_CONFIG } from '@angular/cdk/overlay';
import { MatButtonModule } from '@angular/material/button';
import { MatDialogModule } from '@angular/material/dialog';
import { provideNativeDateAdapter } from '@angular/material/core';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field';
import { MatMenuModule } from '@angular/material/menu';
import { MAT_SELECT_CONFIG } from '@angular/material/select';
import { MatTabsModule } from '@angular/material/tabs';
import { MAT_TOOLTIP_DEFAULT_OPTIONS } from '@angular/material/tooltip';
import { bootstrapApplication, BrowserModule } from '@angular/platform-browser';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideRouter, withRouterConfig } from '@angular/router';
import { TokenInterceptor } from './app/modules/auth0/interceptors/token.interceptor';
import { routes } from './app/app.routes';
import { AppComponent } from './app/app.component';
import { appTooltipDefaults } from './app/shared/utils/tooltip-defaults.utils';
import { installZonelessAuditConsoleFilter } from './app/shared/utils/zoneless-audit-console-filter';
import {
  AppService,
  AssayService,
  CancerTypesService,
  EnrichmentKitsService,
  EthnicitiesService,
  PanelsService,
  PhenotypesService,
  PredictionOptionsService,
  VariantEvidencesSelectionService,
} from './app/store';
import { ClassificationMetadataService } from './app/store/services/classification-metadata/classification-metadata.service';
import { ZonelessDialogActivationService } from './app/shared/services/zoneless-dialog-activation.service';
import { environment } from './environments/environment';

import { registerLicense } from '@syncfusion/ej2-base';

registerLicense(environment.syncfusionActivationKey);

if (environment.production) {
  enableProdMode();
}

const zonelessAuditProviders: EnvironmentProviders[] = environment.zonelessAudit
  ? [provideCheckNoChangesConfig({ exhaustive: true, interval: 1000 })]
  : [];

if (environment.zonelessAudit) {
  installZonelessAuditConsoleFilter();
}

const bootstrap = () =>
  bootstrapApplication(AppComponent, {
    providers: [
      provideZonelessChangeDetection(),
      ...zonelessAuditProviders,
      provideAppInitializer(() => inject(ZonelessDialogActivationService).init()),
      provideRouter(routes, withRouterConfig({ paramsInheritanceStrategy: 'emptyOnly' })),
      importProvidersFrom(
        MatMenuModule,
        MatDialogModule,
        MatButtonModule,
        CommonModule,
        BrowserModule,
        ReactiveFormsModule,
        MatTabsModule,
      ),
      AppService,
      PhenotypesService,
      PanelsService,
      CancerTypesService,
      EthnicitiesService,
      AssayService,
      EnrichmentKitsService,
      VariantEvidencesSelectionService,
      ClassificationMetadataService,
      PredictionOptionsService,
      { provide: OVERLAY_DEFAULT_CONFIG, useValue: { usePopover: false } },
      { provide: MAT_TOOLTIP_DEFAULT_OPTIONS, useValue: appTooltipDefaults },
      {
        provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
        useValue: {
          subscriptSizing: 'dynamic',
        },
      },
      {
        provide: MAT_SELECT_CONFIG,
        useValue: {
          disableOptionCentering: true,
          hideSingleSelectionIndicator: true,
          panelWidth: null,
        },
      },
      { provide: HTTP_INTERCEPTORS, useClass: TokenInterceptor, multi: true },
      provideNativeDateAdapter(),
      provideAnimations(),
      provideHttpClient(withXhr(), withInterceptorsFromDi()),
    ],
  });

if (environment.hmr) {
  // tslint:disable-next-line:no-string-literal
  if ((module as unknown as Record<string, unknown>)['hot']) {
    // hmrBootstrap(module, bootstrap);
  } else {
    console.error('HMR is not enabled for webpack-dev-server!');
    console.log('Are you using the --hmr flag for ng serve?');
  }
} else {
  bootstrap().catch((err) => console.log(err));
}
